/** Day-by-day itinerary — Lee's decision #2/#5: the same per-day slice
 * PlanBar's stats row already reads (daystats.ts's dayStats) fitted out
 * with what a printable day-by-day view needs: plain-word start/end
 * labels, the waypoints that fall inside each day, and a free-text note.
 * Pure — no dates, no weather: TripSchedule/CampForecast are phone-only
 * (the web plan carries no departure/schedule to hang them off), so a web
 * itinerary is deliberately simpler than the phone's ItineraryModel.
 * ui/ItinerarySheet.tsx is the only caller. */

import { haversineM } from "./geo";
import type { DayStat } from "./daystats";
import type { LonLat, Plan } from "./types";

export interface ItineraryDay {
  day: number;
  startLabel: string;
  endLabel: string;
  distanceM: number;
  gainM: number | null;
  etaSeconds: number | null;
  /** The control point this day ends at — null on the final day, which
   * ends the trip rather than an overnight (there's nothing to camp at). */
  campLonLat: LonLat | null;
  waypoints: string[];
  notes: string;
}

/** Index of the control point nearest `at` — how a waypoint gets bucketed
 * into a day below. Ties resolve to the earlier index (strict `<`), which
 * only matters for a waypoint sitting exactly equidistant between two
 * points, an edge case with no "more correct" answer either way. */
function nearestControlPointIndex(plan: Pick<Plan, "points">, at: LonLat): number {
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < plan.points.length; i++) {
    const d = haversineM(plan.points[i], at);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best;
}

/** [low, high] CONTROL-POINT index range for each day, inclusive both
 * ends, that partitions every point index into EXACTLY one day: day 0
 * starts at 0, every later day starts one PAST the previous day's break
 * (the break point itself — camped at, then departed again next morning —
 * counts toward the earlier day, never both), and the final day runs to
 * the route's last point. `dayCount` is trusted to match
 * `dayStats(plan, …).length` (breaks.length + 1) — the only real caller. */
function dayRanges(plan: Pick<Plan, "points" | "dayBreaks">, dayCount: number): [number, number][] {
  const breaks = plan.dayBreaks ?? [];
  const lastIdx = plan.points.length - 1;
  return Array.from({ length: dayCount }, (_, i) => [
    i === 0 ? 0 : breaks[i - 1] + 1,
    i === dayCount - 1 ? lastIdx : breaks[i],
  ]);
}

/** Builds the itinerary from a plan and its already-computed per-day stats
 * (App.tsx passes the same `days` it hands PlanBar, rather than this file
 * re-deriving distance/elevation math — daystats.ts is the one source of
 * that). `notes` is `Plan.dayNotes` (or the sheet's own in-progress edit
 * buffer); shorter than `days` or entirely absent both mean "no note yet"
 * for the missing days, padded to "" rather than throwing. Empty when the
 * plan has under 2 points (dayStats' own contract: nothing to slice). */
export function buildItinerary(plan: Plan, days: DayStat[], notes: string[] | undefined): ItineraryDay[] {
  if (days.length === 0) return [];

  const breaks = plan.dayBreaks ?? [];
  const ranges = dayRanges(plan, days.length);

  // Bucket each waypoint into the day whose control-point range contains
  // its nearest control point — see dayRanges' own doc for why the ranges
  // never overlap or gap, so this `findIndex` always lands in exactly one.
  const waypointsByDay: string[][] = ranges.map(() => []);
  for (const wp of plan.waypoints) {
    const idx = nearestControlPointIndex(plan, wp.at);
    const dayIdx = ranges.findIndex(([low, high]) => idx >= low && idx <= high);
    if (dayIdx >= 0) waypointsByDay[dayIdx].push(wp.name);
  }

  return days.map((d, i) => {
    const isFinal = i === days.length - 1;
    return {
      day: d.day,
      startLabel: i === 0 ? "START" : `Camp ${i}`,
      endLabel: isFinal ? "END" : `Camp ${i + 1}`,
      distanceM: d.distanceM,
      gainM: d.gainM,
      etaSeconds: d.etaSeconds,
      campLonLat: isFinal ? null : plan.points[breaks[i]],
      waypoints: waypointsByDay[i],
      notes: notes?.[i] ?? "",
    };
  });
}
